import { useState } from 'react';
import {
  Grid,
  Button,
  Box,
  Typography,
  useMediaQuery,
  LinearProgress,
  ButtonBase,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import { useHistory } from 'react-router-dom';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import {
  aobfStep1Mobile,
  updateCompanyOnboardingStatusCall,
} from '../../services/company_services';

const styles = () => ({
  label: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '8px',
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
});

const CompanyOnboardingStep1 = () => {
  const { accountDetails, asLoggedUserDetails, editAccountInformation } =
    useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const classes = styles();
  const [plannedUsage, setPlannedUsage] = useState(
    accountDetails.plannedUsage || '',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const history = useHistory();
  const plannedUsageOptions = [
    { label: '🏢 Company', value: 'Company' },
    { label: '👥 Team', value: 'Team' },
    { label: '💡 Side Project', value: 'Side Project' },
    { label: '🤝 Non-Profit', value: 'Non-Profit' },
  ];

  async function sendFormData() {
    try {
      await aobfStep1Mobile({
        CONTACT_ID: asLoggedUserDetails.userId,
        PLANNED_USAGE: plannedUsage,
      });

      return {
        plannedUsage,
      };
    } catch (err) {
      console.log(err);
      setOpenSnackbar(true);
    }
  }

  const disableButton = () => {
    return isLoading || !plannedUsage;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);

      const [payload1] = await Promise.all([
        sendFormData(),
        updateCompanyOnboardingStatusCall({
          COMPANY_ID: accountDetails.id,
          ONBOARDING_STATUS: 'inProcess',
          IS_AOBF: true,
        }),
      ]);
      editAccountInformation({ ...payload1 });

      history.push('/company/onboarding/step2');
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Setup
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#646D7A',
            }}
          >
            Step 1/6
          </Typography>
        </Grid>
        <LinearProgress
          variant="determinate"
          value={(1 / 6) * 100}
          sx={{
            height: 8,
            borderRadius: 999,
            backgroundColor: '#E8EDF4',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              backgroundColor: '#2F6CFB',
            },
          }}
        />
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '24px',
          lineHeight: '28px',
          textAlign: 'center',
          letterSpacing: '-0.5px',
          mb: 2,
        }}
      >
        Let’s Set Up Your Advisory Board
      </Typography>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '14px',
          lineHeight: '20px',
          textAlign: 'center',
          letterSpacing: '-0.5px',
          color: '#646D7A',
        }}
      >
        Estimated Time to Complete:
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          textAlign: 'center',
          letterSpacing: '-0.5px',
          color: '#646D7A',
          mb: 4,
        }}
      >
        Less than 2 minutes
      </Typography>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          textAlign: 'center',
          letterSpacing: '-0.5px',
          color: '#000000',
          mb: 2,
        }}
      >
        What is this advisory board for?
      </Typography>
      <Grid>
        <Grid {...classes.gridItemProps} mb="16px">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {plannedUsageOptions.map((option) => {
              const isSelected = plannedUsage === option.value;
              return (
                <ButtonBase
                  key={option.value}
                  onClick={() => setPlannedUsage(option.value)}
                  disableRipple
                  aria-pressed={isSelected}
                  sx={{
                    width: '100%',
                    justifyContent: 'flex-start',
                    borderRadius: '12px',
                    px: 2,
                    py: 1.5,
                    border: '1px solid #E0E7F1',
                    backgroundColor: isSelected ? '#E4E9F1' : '#F7F9FC',
                    transition: 'background-color 0.2s ease',
                    textAlign: 'left',
                    '&:hover': {
                      backgroundColor: isSelected ? '#DCE3EE' : '#E6EBF3',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: '22px',
                      color: '#111827',
                      letterSpacing: '-0.3px',
                    }}
                  >
                    {option.label}
                  </Typography>
                </ButtonBase>
              );
            })}
          </Box>
        </Grid>
        <Grid
          {...classes.gridItemProps}
          md={5}
          display="flex"
          justifyContent="center"
        >
          <Button
            type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={disableButton()}
          sx={{
            borderRadius: '15px',
            backgroundColor: '#3171F6',
            '&:hover': {
              backgroundColor: '#3171F6',
            },
            height: '48px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 700,
            width: '100%',
            mb: isSM ? '24px' : null,
            my: '20px',
          }}
        >
          {isLoading ? <LoadingLogoSkeleton /> : 'Continue'}
          </Button>
        </Grid>
      </Grid>
      <ACSnackbar
        onClose={() => setOpenSnackbar(false)}
        open={openSnackbar}
        severity="error"
        text="Something went wrong"
      />
    </Box>
  );
};

export default CompanyOnboardingStep1;
